import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { DocumentServicesObject } from '@wix/document-services-types';
import type { TPAMessage, TPAMessageCallback } from '../../types/tpaMessage';
import { SelectedComponentData } from '../utils/handlerUtils';
import { PackagePickerService } from '../../services';

export function createProductHandlers(
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  packagePickerService: PackagePickerService,
  getSelectedComponentAppData: () => Promise<SelectedComponentData>,
) {
  const openBillingPage = async (msg: TPAMessage) => {
    const [selectedComp] =
      await platformApiMethods.editor.selection.getSelectedComponents();

    if (!selectedComp) {
      throw new Error(
        'Component should be selected for openBillingPage handler',
      );
    }

    const origCompData = documentServicesAPI.components.data.get(selectedComp);
    const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
      origCompData?.appDefinitionId,
    );

    const isFullyUpgraded = await packagePickerService.isFullyUpgraded(appData);

    if (isFullyUpgraded) {
      return;
    }

    const options = {
      origin: msg?.data?.referrer || 'settings',
      premiumIntent: msg?.data?.premiumIntent,
    };

    packagePickerService.startUpgradeFlow(appData.appDefinitionId!, options);
  };

  const openReviewInfo = async (msg: TPAMessage) => {
    const { appData } = await getSelectedComponentAppData();

    if (!appData) {
      throw new Error('appData not found for the selected component');
    }

    const iframeUrl = `https://www.wix.com/app-market/add-review/${appData.appDefinitionId}?origin=editorSdk`;
    const componentRef = await platformApiMethods.editor.getCompRefFromCompId?.(
      msg.compId,
    );

    const options = {
      componentRef,
      url: iframeUrl,
      height: 645,
      width: 600,
      shouldHideHeader: false,
      appDefinitionId: msg.data?.appDefinitionId || appData.appDefinitionId,
      applicationId: appData.applicationId,
      modalType: 'appMarketModal',
    } as const;

    platformApiMethods.editor.openAppMarketModal?.(appData, '', options);
  };

  const trackAppUpgrade = async () => {
    const { selectedComp, origCompData } = await getSelectedComponentAppData();

    if (!selectedComp) {
      throw new Error(
        'Component should be selected for trackAppUpgrade handler',
      );
    }

    if (!origCompData?.applicationId) {
      throw new Error('applicationId not found for the selected component');
    }

    platformApiMethods.editor.addAppToTrackForUpgrade?.(
      origCompData.applicationId,
    );
  };

  const getProducts = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    const { appData, origCompData } = await getSelectedComponentAppData();

    const currency = msg.data?.currency;

    if (!msg.data?.appDefinitionId || !origCompData?.isWixTPA) {
      if (!appData?.appDefinitionId) {
        throw new Error(
          'appDefinitionId was not provided in message or retrieved from selected component data',
        );
      }

      documentServicesAPI.tpa.appMarket
        .getPackages(appData.appDefinitionId, appData.instanceId, {
          currency,
        })
        .then(callback, callback);
      return;
    }

    const otherApplicationAppData =
      documentServicesAPI.tpa.app.getDataByAppDefId(msg.data?.appDefinitionId);
    const isOtherAppAvailable =
      otherApplicationAppData &&
      documentServicesAPI.tpa.appMarket.isPermissionsRevoked(
        otherApplicationAppData,
      );
    if (!isOtherAppAvailable) {
      callback({ error: 'app is not installed' });
      return;
    }

    documentServicesAPI.tpa.appMarket
      .getPackages(msg.data.appDefinitionId, appData?.instanceId as string, {
        currency,
      })
      .then(callback, callback);
  };

  return {
    getProducts,
    openBillingPage,
    openReviewInfo,
    trackAppUpgrade,
  };
}
